import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { ROUTE_PATHS, PLANS } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { Check, ChevronRight, MapPin, Shield, Clock } from "lucide-react";

const PERKS = [
  { icon: <MapPin className="w-4 h-4" />, text: "Stałe miejsce w cyfrowym placu" },
  { icon: <Shield className="w-4 h-4" />, text: "Bezpieczny dostęp dla bliskich" },
  { icon: <Clock className="w-4 h-4" />, text: "Pamięć na pokolenia" },
];

export default function Reservation() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plotId = params.get("plot") || "empty1";

  const [selectedPlan, setSelectedPlan] = useState<string>("Free");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  const handleContinue = () => {
    if (!form.firstName || !form.lastName || !form.email) return;
    setStep(2);
  };

  const handleGoToCreator = () => {
    navigate(`${ROUTE_PATHS.KREATOR}?plot=${plotId}&plan=${selectedPlan}`);
  };

  return (
    <Layout>
      <div
        className="min-h-screen"
        style={{
          background: "linear-gradient(180deg, oklch(0.07 0.015 230) 0%, oklch(0.09 0.02 225) 50%, oklch(0.08 0.015 230) 100%)",
        }}
      >
        {/* Górna mgła */}
        <div
          className="pointer-events-none fixed top-0 left-0 right-0 h-48 z-10"
          style={{ background: "linear-gradient(to bottom, oklch(0.07 0.015 230), transparent)" }}
        />

        <div className="container mx-auto px-4 max-w-5xl pt-32 pb-20 relative z-20">
          {/* Nagłówek */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.div variants={staggerItem} className="flex justify-center mb-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
            </motion.div>
            <motion.p
              variants={staggerItem}
              className="text-xs uppercase tracking-[0.35em] mb-3"
              style={{ color: "rgba(140,155,175,0.65)" }}
            >
              {plotId} · Miejsce wolne
            </motion.p>
            <motion.h1
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "rgba(212,175,55,0.75)",
              }}
            >
              Zarezerwuj swoje miejsce
            </motion.h1>
            <motion.p
              variants={staggerItem}
              className="text-base max-w-lg mx-auto"
              style={{ color: "rgba(185,200,215,0.7)" }}
            >
              Wypełnij dane, wybierz plan i przejdź do kreatora, by zaprojektować swój e-grób.
            </motion.p>

            {/* Perks */}
            <motion.div
              variants={staggerItem}
              className="flex flex-wrap justify-center gap-6 mt-6"
            >
              {PERKS.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" style={{ color: "rgba(160,175,200,0.65)" }}>
                  <span style={{ color: "rgba(212,175,55,0.6)" }}>{p.icon}</span>
                  {p.text}
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Stepper */}
          <div className="flex justify-center mb-10">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300"
                  style={{
                    background: step >= s ? "rgba(212,175,55,0.75)" : "oklch(0.12 0.02 220)",
                    color: step >= s ? "rgba(8,9,18,0.95)" : "rgba(140,155,175,0.6)",
                    border: step >= s ? "none" : "1px solid oklch(0.20 0.03 220)",
                  }}
                >
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                <span
                  className="mx-2 text-xs"
                  style={{ color: step >= s ? "rgba(212,175,55,0.65)" : "rgba(120,135,155,0.5)" }}
                >
                  {s === 1 ? "Twoje dane" : "Wybierz plan"}
                </span>
                {s < 2 && (
                  <ChevronRight className="w-4 h-4 mx-1" style={{ color: "rgba(120,135,155,0.35)" }} />
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Formularz */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              transition={springPresets.gentle}
              className="lg:col-span-3 rounded-2xl p-8"
              style={{
                background: "oklch(0.10 0.02 220)",
                border: "1px solid oklch(0.18 0.03 220)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
              }}
            >
              {step === 1 ? (
                <>
                  <h2
                    className="text-xl font-semibold mb-6"
                    style={{ fontFamily: "'Playfair Display', serif", color: "rgba(220,230,245,0.9)" }}
                  >
                    Dane osobowe
                  </h2>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs mb-1.5" style={{ color: "rgba(140,155,175,0.7)" }}>
                        Imię *
                      </label>
                      <Input
                        placeholder="Jan"
                        value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                        className="h-11 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 rounded-lg"
                        style={{ background: "oklch(0.13 0.025 220)", color: "rgba(210,225,240,0.9)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1.5" style={{ color: "rgba(140,155,175,0.7)" }}>
                        Nazwisko *
                      </label>
                      <Input
                        placeholder="Kowalski"
                        value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                        className="h-11 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 rounded-lg"
                        style={{ background: "oklch(0.13 0.025 220)", color: "rgba(210,225,240,0.9)" }}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs mb-1.5" style={{ color: "rgba(140,155,175,0.7)" }}>
                      Adres email *
                    </label>
                    <Input
                      type="email"
                      placeholder="jan@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-11 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 rounded-lg"
                      style={{ background: "oklch(0.13 0.025 220)", color: "rgba(210,225,240,0.9)" }}
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs mb-1.5" style={{ color: "rgba(140,155,175,0.7)" }}>
                      Telefon (opcjonalnie)
                    </label>
                    <Input
                      type="tel"
                      placeholder="+48 123 456 789"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-11 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 rounded-lg"
                      style={{ background: "oklch(0.13 0.025 220)", color: "rgba(210,225,240,0.9)" }}
                    />
                  </div>

                  <div className="flex items-start gap-3 mb-6">
                    <button
                      onClick={() => setAgreed(!agreed)}
                      className="mt-0.5 w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-all"
                      style={{
                        background: agreed ? "rgba(212,175,55,0.78)" : "transparent",
                        border: `1px solid ${agreed ? "rgba(212,175,55,0.78)" : "oklch(0.25 0.03 220)"}`,
                      }}
                    >
                      {agreed && <Check className="w-2.5 h-2.5" style={{ color: "rgba(8,9,18,0.95)" }} />}
                    </button>
                    <p className="text-xs leading-relaxed" style={{ color: "rgba(140,155,175,0.65)" }}>
                      Akceptuję <span style={{ color: "rgba(212,175,55,0.65)" }}>Regulamin serwisu</span> i{" "}
                      <span style={{ color: "rgba(212,175,55,0.65)" }}>Politykę prywatności</span>. Rozumiem cel i
                      charakter platformy e-grób.
                    </p>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springPresets.snappy}
                    onClick={handleContinue}
                    disabled={!form.firstName || !form.lastName || !form.email || !agreed}
                    className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-40"
                    style={{
                      background: "rgba(212,175,55,0.78)",
                      color: "rgba(8,9,18,0.95)",
                      boxShadow: "0 2px 20px rgba(212,175,55,0.15)",
                    }}
                  >
                    Dalej — wybierz plan →
                  </motion.button>
                </>
              ) : (
                <>
                  <h2
                    className="text-xl font-semibold mb-2"
                    style={{ fontFamily: "'Playfair Display', serif", color: "rgba(220,230,245,0.9)" }}
                  >
                    Wybierz plan
                  </h2>
                  <p className="text-xs mb-6" style={{ color: "rgba(140,155,175,0.6)" }}>
                    Aktualnie dostępny jest plan Free. Plany płatne są w przygotowaniu i pojawią się wkrótce.
                  </p>

<div className="space-y-3 mb-6">
                    {PLANS.map((plan, planIndex) => {
                      const isComingSoon = planIndex > 0;
                      return (
                        <div key={plan.name} className="relative">
                          <motion.button
                            whileHover={!isComingSoon ? { scale: 1.01 } : {}}
                            whileTap={!isComingSoon ? { scale: 0.99 } : {}}
                            transition={springPresets.snappy}
                            onClick={() => !isComingSoon && setSelectedPlan(plan.name)}
                            disabled={isComingSoon}
                            className="w-full text-left p-4 rounded-xl transition-all duration-200"
                            style={{
                              background: isComingSoon
                                ? "oklch(0.10 0.015 220)"
                                : selectedPlan === plan.name
                                  ? "oklch(0.14 0.03 45 / 0.4)"
                                  : "oklch(0.12 0.02 220)",
                              border: `1px solid ${
                                isComingSoon
                                  ? "oklch(0.14 0.018 220)"
                                  : selectedPlan === plan.name
                                    ? "rgba(212,175,55,0.4)"
                                    : "oklch(0.20 0.03 220)"
                              }`,
                              opacity: isComingSoon ? 0.5 : 1,
                              cursor: isComingSoon ? "not-allowed" : "pointer",
                              filter: isComingSoon ? "grayscale(0.3)" : "none",
                            }}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-all"
                                  style={{
                                    borderColor: isComingSoon ? "rgba(80,95,115,0.4)" : selectedPlan === plan.name ? "rgba(212,175,55,0.78)" : "rgba(100,115,135,0.5)",
                                    background: (!isComingSoon && selectedPlan === plan.name) ? "rgba(212,175,55,0.78)" : "transparent",
                                  }}
                                >
                                  {!isComingSoon && selectedPlan === plan.name && (
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: "rgba(8,9,18,0.9)" }} />
                                  )}
                                </div>
                                <span className="font-medium text-sm" style={{ color: isComingSoon ? "rgba(130,145,165,0.45)" : "rgba(215,228,245,0.9)" }}>
                                  {plan.name}
                                </span>
                                {/* Badge: Wkrótce lub plan.badge */}
                                {isComingSoon ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full"
                                    style={{ background: "rgba(80,95,115,0.15)", color: "rgba(120,135,155,0.6)", border: "1px solid rgba(100,115,135,0.2)" }}>
                                    Wkrótce
                                  </span>
                                ) : plan.badge ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full"
                                    style={{ background: "rgba(212,175,55,0.15)", color: "rgba(212,175,55,0.75)" }}>
                                    {plan.badge}
                                  </span>
                                ) : null}
                              </div>
                              <span className="text-sm font-semibold" style={{ color: isComingSoon ? "rgba(120,135,155,0.45)" : "rgba(212,175,55,0.8)" }}>
                                {plan.price}
                              </span>
                            </div>
                            <p className="text-xs ml-5.5" style={{ color: isComingSoon ? "rgba(100,115,135,0.4)" : "rgba(130,145,165,0.65)" }}>
                              {plan.storage} · {plan.features[0]}
                            </p>
                          </motion.button>
                          {/* Komunikat pod kartą dla planu coming soon */}
                          {isComingSoon && (
                            <p className="text-[10px] mt-1 ml-1" style={{ color: "rgba(110,125,145,0.5)" }}>
                              🔒 Funkcja w rozbudowie — będzie dostępna wkrótce
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="px-4 py-3 rounded-xl text-sm transition-all hover:bg-white/5"
                      style={{ border: "1px solid oklch(0.22 0.03 220)", color: "rgba(160,175,195,0.7)" }}
                    >
                      ← Wróć
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springPresets.snappy}
                      onClick={handleGoToCreator}
                      className="flex-1 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                      style={{
                        background: "rgba(212,175,55,0.78)",
                        color: "rgba(8,9,18,0.95)",
                        boxShadow: "0 2px 20px rgba(212,175,55,0.15)",
                      }}
                    >
                      Przejdź do kreatora →
                    </motion.button>
                  </div>
                </>
              )}
            </motion.div>

            {/* Sidebar — podgląd miejsca */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="lg:col-span-2 space-y-4"
            >
              {/* Wizualizacja miejsca */}
              <div
                className="rounded-2xl p-6"
                style={{
                  background: "oklch(0.10 0.02 220)",
                  border: "1px solid oklch(0.18 0.03 220)",
                }}
              >
                <p className="text-xs uppercase tracking-widest mb-4" style={{ color: "rgba(140,155,175,0.55)" }}>
                  Wybrane miejsce
                </p>

                {/* Prosta wizualizacja nagrobka */}
                <div className="flex flex-col items-center py-6">
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="relative"
                  >
                    {/* Świeczka */}
                    <div className="flex justify-center mb-1">
                      <motion.div
                        animate={{ opacity: [0.5, 1, 0.6, 1, 0.7] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="flex flex-col items-center"
                      >
                        <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(to top, rgba(212,175,55,0.9), rgba(255,200,50,0.9))" }} />
                        <div className="w-2 h-2 rounded-full blur-[2px] -mt-2" style={{ background: "rgba(255,180,30,0.9)" }} />
                      </motion.div>
                    </div>
                    {/* Nagrobek */}
                    <div
                      className="w-20 h-28 rounded-t-full flex flex-col items-center justify-center relative"
                      style={{
                        background: "linear-gradient(180deg, oklch(0.18 0.035 220), oklch(0.12 0.025 215))",
                        border: "1px solid rgba(212,175,55,0.3)",
                        boxShadow: "0 0 24px rgba(212,175,55,0.08), 0 8px 24px rgba(0,0,0,0.5)",
                      }}
                    >
                      <div className="flex flex-col items-center">
                        <div className="w-px h-6 rounded-full" style={{ background: "rgba(212,175,55,0.4)" }} />
                        <div className="w-4 h-px rounded-full -mt-3.5" style={{ background: "rgba(212,175,55,0.4)" }} />
                      </div>
                      <div className="absolute bottom-3 text-center px-1">
                        <p className="text-xs font-medium" style={{ color: "rgba(212,175,55,0.7)", fontFamily: "'Playfair Display', serif" }}>
                          {form.firstName || "Twoje"}{" "}{form.lastName || "Imię"}
                        </p>
                      </div>
                    </div>
                    {/* Podstawa */}
                    <div className="h-2 w-24 mx-auto rounded-sm -mt-0.5" style={{ background: "oklch(0.15 0.025 220)" }} />
                  </motion.div>
                  <p className="text-xs mt-4" style={{ color: "rgba(140,155,175,0.5)" }}>
                    Miejsce #{plotId}
                  </p>
                </div>
              </div>

              {/* Co dostajesz */}
              <div
                className="rounded-2xl p-5"
                style={{
                  background: "oklch(0.10 0.02 220)",
                  border: "1px solid oklch(0.18 0.03 220)",
                }}
              >
                <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "rgba(140,155,175,0.55)" }}>
                  Co otrzymujesz
                </p>
                {[
                  "Stałe miejsce na Placu Cyfrowym",
                  "Kreator wizualny e-grobu",
                  "Galeria zdjęć i wspomnień",
                  "Dostęp dla bliskich",
                  "Możliwość rozbudowy",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 mb-2.5">
                    <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "rgba(212,175,55,0.6)" }} />
                    <span className="text-xs" style={{ color: "rgba(170,185,205,0.75)" }}>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
